/**
 * Permission System Utilities
 */

import type {
  ProfileRole,
  ResourceType,
  Action,
  Permission,
  PermissionCheck,
  PermissionContext,
} from '../types/permissions'

/**
 * Role Permission Definitions
 */
export const ROLE_PERMISSIONS: Record<ProfileRole, Permission[]> = {
  superadmin: [
    // Platform creator — full cross-agency access
    { resource: 'players', action: 'manage' },
    { resource: 'contracts', action: 'manage' },
    { resource: 'transactions', action: 'manage' },
    { resource: 'invoices', action: 'manage' },
    { resource: 'deals', action: 'manage' },
    { resource: 'messages', action: 'manage' },
    { resource: 'notes', action: 'manage' },
    { resource: 'calendar', action: 'manage' },
    { resource: 'teams', action: 'manage' },
    { resource: 'coaches', action: 'manage' },
    { resource: 'compliance', action: 'manage' },
    { resource: 'settings', action: 'manage' },
    { resource: 'users', action: 'manage' },
  ],

  admin: [
    // Agency CEO/head agent — full access within own agency
    { resource: 'players', action: 'manage' },
    { resource: 'contracts', action: 'manage' },
    { resource: 'transactions', action: 'manage' },
    { resource: 'invoices', action: 'manage' },
    { resource: 'deals', action: 'manage' },
    { resource: 'messages', action: 'manage' },
    { resource: 'notes', action: 'manage' },
    { resource: 'calendar', action: 'manage' },
    { resource: 'teams', action: 'manage' },
    { resource: 'coaches', action: 'manage' },
    { resource: 'compliance', action: 'manage' },
    { resource: 'settings', action: 'manage' },
    { resource: 'users', action: 'read' },
    { resource: 'users', action: 'create' },
    { resource: 'users', action: 'update' },
  ],

  agent: [
    // Full CRUD on own records, read on others
    { resource: 'players', action: 'create' },
    { resource: 'players', action: 'read' },
    {
      resource: 'players',
      action: 'update',
      condition: (ctx?: PermissionContext) => {
        // Can update own players or if no owner
        return !ctx?.ownerId || ctx.ownerId === ctx?.userId
      },
    },
    {
      resource: 'players',
      action: 'delete',
      condition: (ctx?: PermissionContext) => {
        return ctx?.ownerId === ctx?.userId
      },
    },

    { resource: 'contracts', action: 'create' },
    { resource: 'contracts', action: 'read' },
    {
      resource: 'contracts',
      action: 'update',
      condition: (ctx?: PermissionContext) => {
        return !ctx?.ownerId || ctx.ownerId === ctx?.userId
      },
    },
    {
      resource: 'contracts',
      action: 'delete',
      condition: (ctx?: PermissionContext) => {
        return ctx?.ownerId === ctx?.userId
      },
    },

    // Financial access - read only for agents
    { resource: 'transactions', action: 'read' },
    { resource: 'invoices', action: 'read' },

    { resource: 'deals', action: 'create' },
    { resource: 'deals', action: 'read' },
    {
      resource: 'deals',
      action: 'update',
      condition: (ctx?: PermissionContext) => {
        return !ctx?.ownerId || ctx.ownerId === ctx?.userId
      },
    },
    {
      resource: 'deals',
      action: 'delete',
      condition: (ctx?: PermissionContext) => {
        return ctx?.ownerId === ctx?.userId
      },
    },

    // Messaging - full access
    { resource: 'messages', action: 'create' },
    { resource: 'messages', action: 'read' },
    {
      resource: 'messages',
      action: 'update',
      condition: (ctx?: PermissionContext) => {
        return ctx?.userId === ctx?.ownerId // Can only update own messages
      },
    },
    {
      resource: 'messages',
      action: 'delete',
      condition: (ctx?: PermissionContext) => {
        return ctx?.userId === ctx?.ownerId
      },
    },

    { resource: 'notes', action: 'create' },
    { resource: 'notes', action: 'read' },
    {
      resource: 'notes',
      action: 'update',
      condition: (ctx?: PermissionContext) => {
        return ctx?.userId === ctx?.ownerId
      },
    },
    {
      resource: 'notes',
      action: 'delete',
      condition: (ctx?: PermissionContext) => {
        return ctx?.userId === ctx?.ownerId
      },
    },

    { resource: 'calendar', action: 'create' },
    { resource: 'calendar', action: 'read' },
    {
      resource: 'calendar',
      action: 'update',
      condition: (ctx?: PermissionContext) => {
        return ctx?.userId === ctx?.ownerId
      },
    },
    {
      resource: 'calendar',
      action: 'delete',
      condition: (ctx?: PermissionContext) => {
        return ctx?.userId === ctx?.ownerId
      },
    },

    { resource: 'teams', action: 'read' },
    { resource: 'coaches', action: 'read' },
    { resource: 'compliance', action: 'read' },
  ],

  assistant: [
    // Create/update only, no delete, limited financial access
    { resource: 'players', action: 'create' },
    { resource: 'players', action: 'read' },
    { resource: 'players', action: 'update' },
    // No delete

    { resource: 'contracts', action: 'create' },
    { resource: 'contracts', action: 'read' },
    { resource: 'contracts', action: 'update' },
    // No delete

    // No financial access
    // { resource: 'transactions', action: 'read' }, // Commented out - assistants can't see finances
    // { resource: 'invoices', action: 'read' },

    { resource: 'deals', action: 'create' },
    { resource: 'deals', action: 'read' },
    { resource: 'deals', action: 'update' },
    // No delete

    { resource: 'messages', action: 'create' },
    { resource: 'messages', action: 'read' },
    {
      resource: 'messages',
      action: 'update',
      condition: (ctx?: PermissionContext) => {
        return ctx?.userId === ctx?.ownerId
      },
    },
    // No delete

    { resource: 'notes', action: 'create' },
    { resource: 'notes', action: 'read' },
    {
      resource: 'notes',
      action: 'update',
      condition: (ctx?: PermissionContext) => {
        return ctx?.userId === ctx?.ownerId
      },
    },
    // No delete

    { resource: 'calendar', action: 'create' },
    { resource: 'calendar', action: 'read' },
    {
      resource: 'calendar',
      action: 'update',
      condition: (ctx?: PermissionContext) => {
        return ctx?.userId === ctx?.ownerId
      },
    },
    // No delete

    { resource: 'teams', action: 'read' },
    { resource: 'coaches', action: 'read' },
    { resource: 'compliance', action: 'read' },
  ],

  'read only': [
    // Read-only access
    { resource: 'players', action: 'read' },
    { resource: 'contracts', action: 'read' },
    // No financial access
    { resource: 'deals', action: 'read' },
    { resource: 'messages', action: 'read' },
    { resource: 'notes', action: 'read' },
    { resource: 'calendar', action: 'read' },
    { resource: 'teams', action: 'read' },
    { resource: 'coaches', action: 'read' },
    { resource: 'compliance', action: 'read' },
    // No create, update, delete, or manage
  ],
}

/**
 * Check if a role has permission for a resource/action
 */
export function hasPermission(
  role: ProfileRole,
  resource: ResourceType,
  action: Action,
  context?: PermissionContext
): PermissionCheck {
  const permissions = ROLE_PERMISSIONS[role] || []

  // Check for 'manage' permission (grants all actions)
  const managePermission = permissions.find(
    p => p.resource === resource && p.action === 'manage'
  )

  if (managePermission) {
    // Check condition if present
    if (managePermission.condition) {
      const allowed = managePermission.condition(context)
      return {
        allowed,
        reason: allowed ? undefined : 'Condition check failed',
      }
    }
    return { allowed: true }
  }

  // Check for specific action permission
  const permission = permissions.find(
    p => p.resource === resource && p.action === action
  )

  if (!permission) {
    return {
      allowed: false,
      reason: `${role} role does not have ${action} permission for ${resource}`,
    }
  }

  // Check condition if present
  if (permission.condition) {
    const allowed = permission.condition(context)
    return {
      allowed,
      reason: allowed ? undefined : 'Condition check failed',
    }
  }

  return { allowed: true }
}

/**
 * Check if user can perform action on resource
 */
export function can(
  role: ProfileRole | undefined,
  resource: ResourceType,
  action: Action,
  context?: PermissionContext
): boolean {
  if (!role) return false
  return hasPermission(role, resource, action, context).allowed
}

/**
 * Get all permissions for a role
 */
export function getRolePermissions(role: ProfileRole): Permission[] {
  return ROLE_PERMISSIONS[role] || []
}

/**
 * Check if role can manage a resource (has manage permission)
 */
export function canManage(role: ProfileRole | undefined, resource: ResourceType): boolean {
  if (!role) return false
  return can(role, resource, 'manage')
}

/**
 * Get role description
 */
export function getRoleDescription(role: ProfileRole): string {
  const descriptions: Record<ProfileRole, string> = {
    superadmin: 'Platform super administrator with cross-agency access',
    admin: 'Agency administrator with full access within their agency',
    agent: 'Full CRUD on own records, read-only on others, limited financial access',
    assistant: 'Create and update records, no delete or financial access',
    'read only': 'Read-only access to most features',
  }
  return descriptions[role] || 'Unknown role'
}

/**
 * Get allowed actions for a role and resource
 */
export function getAllowedActions(
  role: ProfileRole,
  resource: ResourceType
): Action[] {
  const permissions = ROLE_PERMISSIONS[role] || []
  const actions: Action[] = []

  // Check for manage permission
  if (permissions.some(p => p.resource === resource && p.action === 'manage')) {
    return ['create', 'read', 'update', 'delete', 'manage']
  }

  // Collect specific actions
  permissions
    .filter(p => p.resource === resource)
    .forEach(p => {
      if (p.action !== 'manage' && !actions.includes(p.action)) {
        actions.push(p.action)
      }
    })

  return actions
}

